import ky, { type KyInstance } from 'ky'

import type { BackofficeContext } from '../../framework/context/types'
import type { GridApi, GridApiResponse } from '../../framework/view/types'
import { mapKyHttpError } from '../ky-error'
import { toSearchParams, prepareQueryParams } from '../query'
import {
  parseGridPaginationInfo,
  parseHttpBody,
  parseRecordList,
  type ApiResponse,
} from '../response'

export interface TournamentsApi {
  grid: GridApi
  getFilters: (filters: Record<string, unknown>) => Promise<Record<string, unknown>[]>
  getSingle: (id: unknown, params?: Record<string, unknown>) => Promise<ApiResponse>
  update: (id: unknown, data: Record<string, unknown>) => Promise<ApiResponse>
  create: (data: Record<string, unknown>) => Promise<ApiResponse>
  delete: (id: unknown) => Promise<void>
  getTranslations: (id: unknown, filters?: Record<string, unknown>) => Promise<ApiResponse>
  getFeedTypeMappings: (id: unknown, filters?: Record<string, unknown>) => Promise<ApiResponse>
  getFeedInstanceMappings: (id: unknown, filters?: Record<string, unknown>) => Promise<ApiResponse>
  getApplicationConfigurations: (
    tournamentId: unknown,
    filters?: Record<string, unknown>,
  ) => Promise<Record<string, unknown>[]>
}

export type TournamentsApiDeps = Pick<BackofficeContext, 'read' | 'auth'>

function gridRequestParams(filters: Record<string, unknown>): Record<string, unknown> {
  const params = prepareQueryParams(filters)
  delete params.searchParameter
  return {
    ...params,
    isReadAfterWrite: filters.isReadAfterWrite || false,
    search: filters.searchParameter ?? filters.search,
  }
}

function readAfterWriteParams(filters: Record<string, unknown> = {}): Record<string, unknown> {
  return {
    isReadAfterWrite: filters.isReadAfterWrite || false,
  }
}

function toGridResponse(body: unknown): GridApiResponse {
  const response = (body ?? {}) as ApiResponse
  return {
    data: parseRecordList(response.data),
    paginationInfo: parseGridPaginationInfo(response),
  }
}

export function createTournamentsApi(deps: TournamentsApiDeps, http?: KyInstance): TournamentsApi {
  const { read, auth } = deps

  const client =
    http ??
    ky.create({
      prefix: `${import.meta.env.NXG_SPORTS_AIO_CORE}/tenants/${read.tenantId()}/instances/${read.instance()}/languages/${read.language()}`,
      retry: 0,
      throwHttpErrors: true,
      headers: { Accept: 'application/json' },
      hooks: {
        beforeRequest: [
          ({ request }) => {
            request.headers.set('Authorization', `Bearer ${auth.getAccessToken()}`)
          },
        ],
        beforeError: [({ error }) => mapKyHttpError(error)],
      },
    })

  return {
    grid: {
      get: async (filters) => {
        const body = parseHttpBody(
          await client
            .get('tournaments/grid', { searchParams: toSearchParams(gridRequestParams(filters)) })
            .text(),
        )
        return toGridResponse(body)
      },
      update: async (id, params) =>
        parseHttpBody(
          await client
            .patch(`tournaments/${id}`, {
              body: JSON.stringify(params),
              headers: { 'Content-Type': 'application/merge-patch+json' },
            })
            .text(),
        ) as ApiResponse,
      delete: async (id) => {
        await client.delete(`tournaments/${id}`)
      },
    },
    getFilters: async (filters) => {
      const body = parseHttpBody(
        await client
          .get('tournaments/filters', {
            searchParams: toSearchParams({
              ...prepareQueryParams(filters),
              isReadAfterWrite: filters.isReadAfterWrite || false,
              search: filters.searchParameter ?? filters.search,
            }),
          })
          .text(),
      ) as ApiResponse
      return parseRecordList(body.data)
    },
    getSingle: async (id, params = {}) =>
      parseHttpBody(
        await client
          .get(`tournaments/${id}`, { searchParams: toSearchParams(readAfterWriteParams(params)) })
          .text(),
      ) as ApiResponse,
    update: async (id, data) =>
      parseHttpBody(
        await client
          .patch(`tournaments/${id}`, {
            body: JSON.stringify(data),
            headers: { 'Content-Type': 'application/merge-patch+json' },
          })
          .text(),
      ) as ApiResponse,
    create: async (data) =>
      parseHttpBody(await client.post('tournaments', { json: data }).text()) as ApiResponse,
    delete: async (id) => {
      await client.delete(`tournaments/${id}`)
    },
    getTranslations: async (id, filters = {}) =>
      parseHttpBody(
        await client
          .get(`tournaments/${id}/translations`, {
            searchParams: toSearchParams(readAfterWriteParams(filters)),
          })
          .text(),
      ) as ApiResponse,
    getFeedTypeMappings: async (id, filters = {}) =>
      parseHttpBody(
        await client
          .get(`tournaments/${id}/feed-type-mappings`, {
            searchParams: toSearchParams(readAfterWriteParams(filters)),
          })
          .text(),
      ) as ApiResponse,
    getFeedInstanceMappings: async (id, filters = {}) =>
      parseHttpBody(
        await client
          .get(`tournaments/${id}/feed-instance-mappings`, {
            searchParams: toSearchParams(readAfterWriteParams(filters)),
          })
          .text(),
      ) as ApiResponse,
    getApplicationConfigurations: async (tournamentId, filters = {}) => {
      const body = parseHttpBody(
        await client
          .get(`tournaments/${tournamentId}/application-configurations`, {
            searchParams: toSearchParams(readAfterWriteParams(filters)),
          })
          .text(),
      ) as ApiResponse
      return parseRecordList(body.data)
    },
  }
}
